import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Trash2, Users } from 'lucide-react';
import { apiClient } from '../services/api';
import { Instance, InstanceMemberResponse } from '../types/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface ShareCollectionModalProps {
  collection: Instance;
  onClose: () => void;
  /** Called with the shared email when share succeeds; parent should close modal and show success. */
  onShareSuccess?: (email: string) => void;
}

function getErrorMessage(err: any): string {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    return typeof first === 'string' ? first : first?.msg ?? String(first);
  }
  return err.message || 'Failed to share collection';
}

export const ShareCollectionModal: React.FC<ShareCollectionModalProps> = ({
  collection,
  onClose,
}) => {
  useBodyScrollLock();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [members, setMembers] = useState<InstanceMemberResponse[]>([]);
  const [isMembersLoading, setIsMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<number | null>(null);

  const fetchMembers = useCallback(async () => {
    try {
      setMembersError(null);
      const data = await apiClient.getInstanceMembers(collection.id);
      setMembers(data.items);
    } catch (err: any) {
      console.error('Error fetching instance members:', err);
      setMembersError('Failed to load shared users.');
    } finally {
      setIsMembersLoading(false);
    }
  }, [collection.id]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter an email address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await apiClient.addInstanceMember(collection.id, trimmed);
      setEmail('');
      setSuccessMessage(`Shared with ${trimmed}`);
      setTimeout(() => setSuccessMessage(null), 4000);
      await fetchMembers();
      return;
    } catch (err: any) {
      const status = err.response?.status;
      if (status >= 200 && status < 300) {
        setEmail('');
        setSuccessMessage(`Shared with ${trimmed}`);
        setTimeout(() => setSuccessMessage(null), 4000);
        await fetchMembers();
        return;
      }
      const detail = err.response?.data?.detail;
      if (status === 404 && detail === 'No user found with this email') {
        setError("This user doesn't have a VocaBranch account yet. Ask them to sign up first!");
      } else if (status === 403) {
        const message = getErrorMessage(err);
        setError(message || 'Only collection owners can share.');
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (member: InstanceMemberResponse) => {
    setRemovingUserId(member.user_id);
    try {
      await apiClient.removeInstanceMember(collection.id, member.user_id);
      setMembers(prev => prev.filter(m => m.user_id !== member.user_id));
    } catch (err: any) {
      console.error('Error removing member:', err);
      setMembersError(getErrorMessage(err));
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Share Collection</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              Add a viewer by email. They will have read-only access to <strong>{collection.name}</strong>.
            </p>
            <div>
              <label htmlFor="share-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="share-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(null); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="colleague@example.com"
                disabled={isSubmitting}
                autoFocus
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3">
                <div className="text-sm text-red-700">{error}</div>
              </div>
            )}

            {successMessage && (
              <div className="rounded-md bg-green-50 p-3">
                <div className="text-sm text-green-700">{successMessage}</div>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Done
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Share
              </button>
            </div>
          </form>

        {/* Shared users list */}
        <div className="mt-6 border-t border-gray-200 pt-4 overflow-y-auto">
          <div className="flex items-center gap-2 mb-3">
            <Users className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-900">Shared with</h3>
          </div>

          {isMembersLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading...</span>
            </div>
          ) : membersError ? (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{membersError}</p>
            </div>
          ) : members.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">
              This collection hasn't been shared with anyone yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {members.map((member) => (
                <li
                  key={member.id}
                  className="flex items-center justify-between py-2 px-3 rounded-md bg-gray-50"
                >
                  <span className="text-sm text-gray-800 truncate">
                    {member.email ?? `User #${member.user_id}`}
                  </span>
                  <button
                    onClick={() => handleRemoveMember(member)}
                    disabled={removingUserId === member.user_id}
                    className="ml-2 p-1 text-gray-400 hover:text-red-600 rounded transition-colors disabled:opacity-50"
                    title="Remove access"
                  >
                    {removingUserId === member.user_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
